import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';
import { useScrollReveal } from '@/hooks/useScrollReveal';

type SortKey = 'newest' | 'oldest' | 'price_asc' | 'price_desc';

const SORT_OPTIONS: { id: SortKey; label: string }[] = [
  { id: 'newest', label: 'Сначала новые' },
  { id: 'oldest', label: 'Сначала старые' },
  { id: 'price_asc', label: 'Дешевле' },
  { id: 'price_desc', label: 'Дороже' },
];

const formatViewedAt = (ts: number): string => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин. назад`;
  if (hours < 24) return `${hours} ч. назад`;
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн. назад`;
  return new Date(ts).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const ProfileRecentlyViewed = () => {
  const navigate = useNavigate();
  const { items, removeItem, clearAll } = useRecentlyViewed();
  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [sort, setSort] = useState<SortKey>('newest');
  const [sortOpen, setSortOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.05 });

  const sorted = [...items].sort((a, b) => {
    if (sort === 'oldest') return a.viewedAt - b.viewedAt;
    if (sort === 'price_asc') return a.price - b.price;
    if (sort === 'price_desc') return b.price - a.price;
    return b.viewedAt - a.viewedAt;
  });

  const currentSortLabel = SORT_OPTIONS.find(o => o.id === sort)?.label ?? '';

  const handleClear = () => {
    if (confirmClear) {
      clearAll();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
    }
  };

  return (
    <div ref={sectionRef as React.RefObject<HTMLDivElement>}>
      {/* Header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div>
          <h2 className="font-orbitron font-black text-xl text-white">ИСТОРИЯ ПРОСМОТРОВ</h2>
          <p className="font-mono text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {items.length > 0
              ? `${items.length} ${items.length === 1 ? 'товар' : items.length < 5 ? 'товара' : 'товаров'} · последние 20 просмотров`
              : 'Нет просмотренных товаров'}
          </p>
        </div>

        {items.length > 0 && (
          <div className="flex items-center gap-2">
            {/* Sort */}
            <div className="relative">
              <button
                onClick={() => setSortOpen(v => !v)}
                className="whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
              >
                <i className="ri-sort-desc text-sm" />
                {currentSortLabel}
                <i className={`text-white/30 transition-transform ${sortOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              </button>
              {sortOpen && (
                <div
                  className="absolute right-0 top-full mt-1.5 w-44 rounded-xl overflow-hidden z-20"
                  style={{ background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => { setSort(opt.id); setSortOpen(false); }}
                      className="w-full text-left px-4 py-2.5 font-mono text-xs cursor-pointer transition-all whitespace-nowrap"
                      style={sort === opt.id
                        ? { color: '#00f5ff', background: 'rgba(0,245,255,0.08)' }
                        : { color: 'rgba(255,255,255,0.5)' }
                      }
                    >
                      {sort === opt.id && <i className="ri-check-line mr-2" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear all */}
            <button
              onClick={handleClear}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all"
              style={confirmClear
                ? { background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }
                : { border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }
              }
              onMouseEnter={e => { if (!confirmClear) { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; } }}
              onMouseLeave={e => { if (!confirmClear) { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; } }}
            >
              <i className={confirmClear ? 'ri-alert-line' : 'ri-delete-bin-line'} />
              {confirmClear ? 'Подтвердить?' : 'Очистить'}
            </button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {items.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-20 text-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1) 0.1s',
          }}
        >
          <div
            className="w-20 h-20 flex items-center justify-center rounded-2xl mb-5"
            style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)' }}
          >
            <i className="ri-history-line text-3xl" style={{ color: '#00f5ff' }} />
          </div>
          <h3 className="font-orbitron font-bold text-lg text-white mb-2">История пуста</h3>
          <p className="font-mono text-sm mb-6 max-w-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Просматривайте товары в каталоге — они будут автоматически сохраняться здесь
          </p>
          <button
            onClick={() => navigate('/catalog')}
            className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-xl font-orbitron font-bold text-sm cursor-pointer transition-all"
            style={{ background: '#00f5ff', color: '#050508', boxShadow: '0 0 15px rgba(0,245,255,0.4)' }}
          >
            <i className="ri-medicine-bottle-line" />
            Перейти в каталог
          </button>
        </div>
      ) : (
        <>
          {/* Timeline hint */}
          <div
            className="flex items-center gap-3 mb-5 px-4 py-3 rounded-xl"
            style={{
              background: 'rgba(0,245,255,0.04)',
              border: '1px solid rgba(0,245,255,0.12)',
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1) 0.05s',
            }}
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ background: 'rgba(0,245,255,0.12)' }}>
              <i className="ri-information-line text-sm" style={{ color: '#00f5ff' }} />
            </div>
            <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              История хранится локально на вашем устройстве. Последние {items.length} из 20 просмотренных товаров.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {sorted.map((item, index) => {
              const inCart = hasItem(item.id);
              const fav = isFavorite(item.id);

              return (
                <div
                  key={item.id}
                  className="rounded-2xl overflow-hidden group relative flex flex-col"
                  style={{
                    border: `1px solid ${item.color}18`,
                    background: 'rgba(255,255,255,0.02)',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                    transition: `opacity 0.5s cubic-bezier(0.22,1,0.36,1) ${index * 55}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${index * 55}ms`,
                  }}
                >
                  {/* Discount badge */}
                  {item.discount && item.discount > 0 ? (
                    <div className="absolute top-3 left-3 z-10">
                      <span
                        className="px-2.5 py-0.5 rounded-full font-orbitron font-black text-[10px]"
                        style={{ background: item.color, color: '#050508', boxShadow: `0 0 8px ${item.color}80` }}
                      >
                        -{item.discount}%
                      </span>
                    </div>
                  ) : null}

                  {/* Top-right actions */}
                  <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
                    {/* Favorite */}
                    <button
                      onClick={() => toggleFavorite({ id: item.id, name: item.name, price: item.price, oldPrice: item.oldPrice, image: item.image, color: item.color, category: item.category, rating: item.rating, discount: item.discount, inStock: true })}
                      className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
                      style={{
                        background: fav ? 'rgba(255,45,120,0.2)' : 'rgba(0,0,0,0.45)',
                        border: fav ? '1px solid rgba(255,45,120,0.5)' : '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(4px)',
                      }}
                      title={fav ? 'Убрать из избранного' : 'В избранное'}
                    >
                      <i className={fav ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: fav ? '#ff2d78' : 'rgba(255,255,255,0.4)', fontSize: '13px' }} />
                    </button>
                    {/* Remove from history */}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 opacity-0 group-hover:opacity-100"
                      style={{
                        background: 'rgba(0,0,0,0.45)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(4px)',
                      }}
                      title="Удалить из истории"
                    >
                      <i className="ri-close-line text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }} />
                    </button>
                  </div>

                  {/* Image */}
                  <div
                    className="relative h-44 flex items-center justify-center overflow-hidden cursor-pointer"
                    style={{ background: `linear-gradient(135deg, #0a0a14, ${item.color}08)` }}
                    onClick={() => navigate(`/product/${item.id}`)}
                  >
                    <div
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity"
                      style={{ background: item.color, filter: 'blur(16px)' }}
                    />
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-28 h-28 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1"
                      style={{ filter: `drop-shadow(0 8px 16px ${item.color}50)` }}
                    />
                    {/* Viewed-at badge */}
                    <div
                      className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg"
                      style={{ background: 'rgba(5,5,8,0.75)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <i className="ri-time-line text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        {formatViewedAt(item.viewedAt)}
                      </span>
                    </div>
                    <div
                      className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: `linear-gradient(90deg, transparent, ${item.color}, transparent)` }}
                    />
                  </div>

                  {/* Info */}
                  <div className="p-4 flex flex-col flex-1">
                    {item.category && (
                      <span
                        className="font-mono text-[9px] px-2 py-0.5 rounded-full mb-2 self-start"
                        style={{ background: `${item.color}12`, color: item.color, border: `1px solid ${item.color}25` }}
                      >
                        {item.category}
                      </span>
                    )}

                    <h3
                      className="font-orbitron font-bold text-xs text-white leading-snug mb-2 line-clamp-2 cursor-pointer hover:text-white/70 transition-colors flex-1"
                      onClick={() => navigate(`/product/${item.id}`)}
                    >
                      {item.name}
                    </h3>

                    {item.rating && (
                      <div className="flex items-center gap-1 mb-3">
                        {[1,2,3,4,5].map(s => (
                          <i key={s} className={`text-[9px] ${s <= Math.round(item.rating!) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                        ))}
                        <span className="font-mono text-[10px] text-white/30 ml-0.5">{item.rating}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <div
                          className="font-orbitron font-black text-lg"
                          style={{ color: item.color, textShadow: `0 0 8px ${item.color}60` }}
                        >
                          {item.price.toLocaleString()} ₽
                        </div>
                        {item.oldPrice && (
                          <div className="font-mono text-[10px] text-white/25 line-through">
                            {item.oldPrice.toLocaleString()} ₽
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => !inCart && addItem({ id: item.id, name: item.name, price: item.price, oldPrice: item.oldPrice, image: item.image, color: item.color, category: item.category })}
                        className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                        style={inCart
                          ? { background: item.color, color: '#050508', boxShadow: `0 0 10px ${item.color}` }
                          : { border: `1px solid ${item.color}`, color: item.color }
                        }
                      >
                        <i className={inCart ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                        {inCart ? 'В корзине' : 'В корзину'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div
            className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl"
            style={{
              background: 'rgba(0,245,255,0.03)',
              border: '1px solid rgba(0,245,255,0.1)',
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1) 0.35s',
            }}
          >
            <div>
              <p className="font-orbitron font-bold text-sm text-white">Добавить всё в корзину</p>
              <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {items.filter(i => !hasItem(i.id)).length} товаров ещё не в корзине
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/catalog')}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all"
                style={{ border: '1px solid rgba(0,245,255,0.25)', color: '#00f5ff' }}
              >
                <i className="ri-store-2-line" />
                В каталог
              </button>
              <button
                onClick={() => {
                  items
                    .filter(i => !hasItem(i.id))
                    .forEach(i => addItem({ id: i.id, name: i.name, price: i.price, oldPrice: i.oldPrice, image: i.image, color: i.color, category: i.category }));
                }}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-orbitron font-bold text-sm cursor-pointer transition-all"
                style={{ background: '#00f5ff', color: '#050508', boxShadow: '0 0 15px rgba(0,245,255,0.35)' }}
              >
                <i className="ri-shopping-bag-line" />
                Добавить всё
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileRecentlyViewed;
